import type { Identity } from '../../types/auth';
import type {
  DataSourceCategory,
  DataSourceStatus,
  ExportColumn,
  ExportField,
  ExportRecord,
  ExportSection,
  ExportSource,
  ExportValueFormat,
  SubjectIdentity,
} from '../../types/dataController';
import { compactUuid, dashedUuid } from '../../utils/dataControllerFormat';

/** Thrown by a source to report "not queried" (permission missing, identifier unknown) rather than an error. */
export class SkipSource extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = 'SkipSource';
  }
}

export interface SourceOutput {
  sections: ExportSection[];
  recordCount: number;
  notes?: string[];
}

export interface ExportContext {
  /** Dashed, lower-case. */
  uuid: string;
  uuidCompact: string;
  /** Enriched by phase-1 (identity) sources before phase 2 runs. */
  subject: SubjectIdentity;
  /** Cluster ids discovered from Redis (empty when Redis is unreachable). */
  clusterIds: string[];
  /** The staff member running the export. */
  viewer: Identity | null;
  addIps: (ips: (string | null | undefined)[]) => void;
  addAlias: (name: string | null | undefined) => void;
}

export interface SourceDefinition {
  id: string;
  title: string;
  system: string;
  description: string;
  category: DataSourceCategory;
  /** Phase 1 sources resolve identity (Discord ID, email, username, IPs) before phase 2 fans out. */
  phase: 1 | 2;
  run: (ctx: ExportContext) => Promise<SourceOutput>;
}

export const isSameUuid = (a: string | null | undefined, b: string): boolean => !!a && compactUuid(String(a)) === compactUuid(b);

export const ROW_CAP = 5000;

const ACRONYMS = new Set(['uuid', 'id', 'ip', 'url', 'fqdn', 'es', 'r2', 'kv', 'd1', 'mc', 'jti', 'wip', 'mvp']);

/** `snake_case`, `camelCase`, `kebab-case` and `@keys` split into lower-case word tokens. */
export const tokens = (key: string): string[] =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/** `snake_case`, `camelCase` and `kebab-case` keys become sentence-case labels ("Last known IP"). */
export const humanize = (key: string): string => {
  const words = tokens(key);
  if (words.length === 0) return key;
  return words.map((w, i) => (ACRONYMS.has(w) ? w.toUpperCase() : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
};

const TIME_KEYS = new Set([
  'timestamp', 'created', 'expires', 'enforced', 'date', 'start_time', 'end_time', 'due_date', 'scheduled_start', 'scheduled_end', 'used_at', 'contact_date',
  'response_date', 'start_date', 'end_date', 'ts_ingested', '@timestamp', 'time', 'first_vote', 'last_vote', 'firstjoinedtime', 'lastjoinedtime', 'added', 'saledate',
  'next_occurrence', 'last_occurrence', 'jury_started_at', 'last_login', 'built_at', 'observed_at', 'lastupdated', 'purchase_datetime', 'rolled_up_at',
]);
const JSON_KEYS = new Set(['details', 'metadata', 'data_json', 'players_json', 'response_json', 'source_json', 'deal_terms', 'platform_links', 'value', 'specialties', 'commands', 'policies', 'raw', 'player_entry', 'chapter_groups_json', 'changelog', 'totals', 'items', 'permissionusehistory', 'location', 'dimensions', 'coordinates']);
const BOOL_KEYS = new Set(['confidential', 'appealable', 'publish', 'disabled', 'email_verified', 'self_assigned', 'pinned', 'is_checked', 'is_archived', 'is_blocked', 'is_default', 'is_collapsed', 'is_active', 'discord_synced', 'v2_ban_made_unappealable', 'banned', 'muted', 'pvp', 'iswhitelisted', 'is_whitelisted', 'has_profile', 'is_staff', 'present', 'passed', 'truncated', 'universalchatenabled', 'gamemessagesenabled', 'prefixeffectenabled']);
/** A key is an identifier (monospace) when one of its word tokens is one of these. */
const MONO_TOKENS = new Set(['uuid', 'id', 'discord', 'email', 'ip', 'key', 'hash', 'jti', 'signature', 'actor', 'creator', 'banner', 'uploader', 'by', 'author', 'reviewer', 'assignee', 'member', 'user', 'seller', 'buyer', 'owner', 'from', 'to', 'fqdn', 'ref', 'sub', 'counterparty']);

/** Arrays of scalars print joined on one line (as text), not as JSON. */
const isPrimitiveList = (v: unknown): boolean => Array.isArray(v) && v.every((x) => x === null || typeof x !== 'object');

/** Column format from the key name and a sample of values. */
export const inferFormat = (key: string, samples: unknown[]): ExportValueFormat => {
  const k = key.toLowerCase();
  if (JSON_KEYS.has(k) || k.endsWith('_json')) return 'json';
  if (BOOL_KEYS.has(k)) return 'bool';
  if (TIME_KEYS.has(k) || k.endsWith('_at') || k.endsWith('_time') || k.endsWith('time')) {
    const strings = samples.filter((v): v is string => typeof v === 'string');
    if (strings.length > 0 && strings.every((v) => /\d{4}-\d{2}-\d{2}/.test(v))) return 'datetime-iso';
    return 'datetime';
  }
  if (samples.some((v) => v !== null && typeof v === 'object' && !isPrimitiveList(v))) return 'json';
  if (k === 'size' || k === 'bytes') return 'bytes';
  if (k === 'playtime' || k === 'seconds') return samples.every((v) => typeof v === 'number') ? 'duration-s' : 'text';
  if (tokens(key).some((t) => MONO_TOKENS.has(t))) return 'mono';
  if (samples.length > 0 && samples.every((v) => typeof v === 'number')) return 'number';
  return 'text';
};

const ID_KEYS = ['id', 'ban_uuid', 'run_id', 'operation_id', 'record_key', 'grant_id', 'ticket_id', 'event_id', 'card_id', 'incident_id'];

/** Prefer meaningful columns first in tables and record cards. */
const orderKeys = (keys: string[]): string[] => {
  const score = (k: string) => (ID_KEYS.includes(k) ? 0 : /uuid|discord|email|username|name|title/.test(k) ? 1 : /_at$|timestamp|created|expires|date|time/.test(k) ? 3 : 2);
  return [...keys].sort((a, b) => score(a) - score(b) || keys.indexOf(a) - keys.indexOf(b));
};

export interface AutoSectionOptions {
  description?: string;
  /** Upstream total when rows were capped. */
  total?: number;
  /** Force `records` (cards) or `table`. Default: table up to 6 populated columns. */
  as?: 'table' | 'records';
  formats?: Record<string, ExportValueFormat>;
  labels?: Record<string, string>;
  /** Keys to leave out of the printed section (they stay in the JSON export). */
  omit?: string[];
  /** Keys to print first, in this order. */
  first?: string[];
  recordTitle?: (row: Record<string, unknown>, index: number) => string;
}

/**
 * Build a table (narrow rows) or record cards (wide rows) from raw rows. Columns that are null
 * in every row are dropped from the printed section; the JSON export keeps them.
 */
export const autoSection = (title: string, rows: Record<string, unknown>[], options: AutoSectionOptions = {}): ExportSection => {
  const omit = new Set(options.omit ?? []);
  const keySet = new Set<string>();
  for (const row of rows) for (const k of Object.keys(row)) if (!omit.has(k)) keySet.add(k);
  const populated = [...keySet].filter((k) => rows.some((r) => r[k] !== null && r[k] !== undefined && r[k] !== ''));
  const ordered = [...(options.first ?? []).filter((k) => populated.includes(k)), ...orderKeys(populated.filter((k) => !(options.first ?? []).includes(k)))];
  const formatOf = (k: string) => options.formats?.[k] ?? inferFormat(k, rows.slice(0, 25).map((r) => r[k]).filter((v) => v !== null && v !== undefined));
  const labelOf = (k: string) => options.labels?.[k] ?? humanize(k);

  const asRecords = options.as === 'records' || (options.as !== 'table' && ordered.length > 6);
  if (asRecords) {
    const records: ExportRecord[] = rows.map((row, i) => ({
      title: options.recordTitle ? options.recordTitle(row, i) : recordTitle(row, i),
      fields: ordered.filter((k) => row[k] !== null && row[k] !== undefined && row[k] !== '').map((k) => ({ label: labelOf(k), value: row[k], format: formatOf(k) })),
    }));
    return { kind: 'records', title, description: options.description, records, total: options.total };
  }
  const columns: ExportColumn[] = ordered.map((k) => {
    const format = formatOf(k);
    const list = rows.slice(0, 25).some((r) => isPrimitiveList(r[k]) && (r[k] as unknown[]).length > 0);
    const width = format === 'json' ? 3 : format === 'datetime' || format === 'datetime-iso' || format === 'datetime-ms' || format === 'datetime-s' ? 1.35 : /message|content|reason|description|command|text|notes?$/.test(k) ? 2.6 : format === 'mono' ? 1.6 : list ? 1.6 : 1;
    return { key: k, label: labelOf(k), format, width };
  });
  return { kind: 'table', title, description: options.description, columns, rows, total: options.total };
};

const recordTitle = (row: Record<string, unknown>, index: number): string => {
  for (const k of ID_KEYS) {
    const v = row[k];
    if (typeof v === 'string' || typeof v === 'number') return `${humanize(k)} ${String(v).length > 40 ? `${String(v).slice(0, 37)}…` : v}`;
  }
  return `Record ${index + 1}`;
};

/** Key/value section from an object (nested objects are printed as JSON). */
export const fieldsSection = (title: string, obj: Record<string, unknown>, options: { description?: string; omit?: string[]; formats?: Record<string, ExportValueFormat>; labels?: Record<string, string>; first?: string[] } = {}): ExportSection => {
  const omit = new Set(options.omit ?? []);
  const keys = Object.keys(obj).filter((k) => !omit.has(k));
  const ordered = [...(options.first ?? []).filter((k) => keys.includes(k)), ...keys.filter((k) => !(options.first ?? []).includes(k))];
  const fields: ExportField[] = ordered.map((k) => ({ label: options.labels?.[k] ?? humanize(k), value: obj[k], format: options.formats?.[k] ?? inferFormat(k, [obj[k]]) }));
  return { kind: 'fields', title, description: options.description, fields };
};

export const jsonSection = (title: string, value: unknown, description?: string): ExportSection => ({ kind: 'json', title, description, value });

/** Redis stream ids are `<unix ms>-<seq>`. */
export const streamIdToMs = (id: string): number | null => {
  const ms = Number(String(id).split('-')[0]);
  return Number.isFinite(ms) && ms > 0 ? ms : null;
};

export const cap = <T>(rows: T[], limit = ROW_CAP): { rows: T[]; total: number } => ({ rows: rows.length > limit ? rows.slice(0, limit) : rows, total: rows.length });

/** Daily vote buckets folded into calendar months ("2026-09"), oldest first; empty and malformed buckets are dropped. */
export const monthlyVoteBuckets = (points: { period: string; vote_count: number }[]): { month: string; vote_count: number }[] => {
  const months = new Map<string, number>();
  for (const point of points) {
    const month = String(point.period).slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(month) || !(Number(point.vote_count) > 0)) continue;
    months.set(month, (months.get(month) ?? 0) + Number(point.vote_count));
  }
  return [...months].sort(([a], [b]) => a.localeCompare(b)).map(([month, vote_count]) => ({ month, vote_count }));
};

export const withTimeout = <T>(promise: Promise<T>, ms: number, label: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)} s`)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });

export const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export const statusOf = (output: SourceOutput): DataSourceStatus => (output.recordCount > 0 ? 'ok' : 'empty');

export const finishSource = (def: SourceDefinition, output: SourceOutput, startedAt: number): ExportSource => ({
  id: def.id,
  title: def.title,
  system: def.system,
  description: def.description,
  category: def.category,
  status: statusOf(output),
  recordCount: output.recordCount,
  sections: output.sections,
  notes: output.notes,
  fetchedAt: Date.now(),
  durationMs: Date.now() - startedAt,
});

export const failSource = (def: SourceDefinition, error: unknown, startedAt: number): ExportSource => ({
  id: def.id,
  title: def.title,
  system: def.system,
  description: def.description,
  category: def.category,
  status: error instanceof SkipSource ? 'skipped' : 'error',
  error: errorMessage(error),
  recordCount: 0,
  sections: [],
  fetchedAt: Date.now(),
  durationMs: Date.now() - startedAt,
});

/** Does a fetch error look like "not found" (upstream has nothing for this player)? */
export const isNotFound = (e: unknown): boolean => {
  const msg = errorMessage(e).toLowerCase();
  return msg.includes('404') || msg.includes('not found');
};

export const normaliseUuidInput = (raw: string): string => dashedUuid(raw.trim().toLowerCase());
